import type { CanvasCard, DataTableCardData, PageLayout, ReportPage, TableRow } from "store/types";

export interface SplitPageMeta extends ReportPage {
  isContinuation: boolean;
  originalPageIndex: number;
}

/** Mirrors TIER_STYLES from DataTableCard — used to estimate row heights. */
const TABLE_TIER_METRICS: Record<string, { bodyFont: number; headerFont: number; padV: number; padH: number }> = {
  F: { bodyFont: 13, headerFont: 11, padV: 10, padH: 16 },
  M: { bodyFont: 11, headerFont: 9, padV: 8, padH: 12 },
  S: { bodyFont: 9, headerFont: 8, padV: 6, padH: 9 }
};

function getTableTier(widthPercent: number): string {
  if (widthPercent >= 60) return "F";
  if (widthPercent >= 38) return "M";
  return "S";
}

/**
 * Estimates a single row's pixel height based on actual cell content.
 * Finds the cell with the most text wrapping and uses it to determine
 * the row height. This is critical for medium/small cards where columns
 * are narrow and text wraps heavily.
 */
function estimateRowH(
  row: TableRow,
  colKeys: string[],
  textWidthPx: number,
  charWidth: number,
  singleLineH: number,
  padV: number
): number {
  let maxLines = 1;

  for (let i = 0; i < colKeys.length; i++) {
    let text: string;
    if (i === 0) {
      text = row.title || "";
    } else {
      const val = row.valueData?.[colKeys[i]] ?? (row as Record<string, unknown>)[colKeys[i]];
      if (val == null) continue;
      text = String(val);
    }
    if (text.length <= 1) continue;
    const lines = Math.ceil((text.length * charWidth) / Math.max(textWidthPx, 1));
    maxLines = Math.max(maxLines, lines);
  }

  return Math.ceil(singleLineH * maxLines) + 2 * padV + 1;
}

function getHeaderRowH(headerFont: number, padV: number): number {
  return Math.ceil(headerFont * 1.5) + 2 * padV + 2;
}

// ─── Expand-aware height helpers ────────────────────────────────────────────

const EMPTY_SET: Set<string> = new Set();
const EMPTY_OFFSETS: Record<string, number> = {};

/**
 * Sum pixel heights of visible rows including children of expanded rows.
 */
function measureVisibleHeight(
  rows: TableRow[],
  expanded: Set<string>,
  parentKey: string,
  offsets: Record<string, number>,
  rowHFn: (row: TableRow) => number
): number {
  const offset = offsets[parentKey] ?? 0;
  let total = 0;
  for (let i = 0; i < rows.length; i++) {
    const rowKey = `${parentKey}-${i + offset}`;
    total += rowHFn(rows[i]);
    if (rows[i].children?.length && expanded.has(rowKey)) {
      total += measureVisibleHeight(rows[i].children!, expanded, rowKey, offsets, rowHFn);
    }
  }
  return total;
}

interface SliceResult {
  taken: TableRow[];
  remaining: TableRow[];
  remainingOffsets: Record<string, number>;
}

/**
 * Slice rows so the total visible pixel height fits within `budget`.
 * When a parent's expanded children don't fully fit, splits the children
 * across pages rather than moving the entire parent.
 *
 * The first row is always taken even if it exceeds the budget, to guarantee
 * forward progress and avoid infinite loops.
 */
function sliceByHeight(
  rows: TableRow[],
  budget: number,
  expanded: Set<string>,
  parentKey: string,
  offsets: Record<string, number>,
  rowHFn: (row: TableRow) => number
): SliceResult {
  const topOffset = offsets[parentKey] ?? 0;
  let used = 0;

  for (let i = 0; i < rows.length; i++) {
    const origIdx = i + topOffset;
    const rowKey = `${parentKey}-${origIdx}`;
    const row = rows[i];
    const rowH = rowHFn(row);
    const hasExpandedChildren = !!(row.children?.length && expanded.has(rowKey));

    if (!hasExpandedChildren) {
      if (used + rowH > budget && i > 0) {
        return {
          taken: rows.slice(0, i),
          remaining: rows.slice(i),
          remainingOffsets: { [parentKey]: origIdx }
        };
      }
      used += rowH;
      continue;
    }

    // Parent with expanded children
    const childrenH = measureVisibleHeight(row.children!, expanded, rowKey, offsets, rowHFn);
    const totalCost = rowH + childrenH;

    if (used + totalCost <= budget) {
      used += totalCost;
      continue;
    }

    // Doesn't fit entirely — try to split children
    const remainingBudget = budget - used - rowH;

    if (remainingBudget > 0) {
      const childResult = sliceByHeight(
        row.children!, remainingBudget, expanded, rowKey, offsets, rowHFn
      );

      if (childResult.taken.length > 0) {
        // Verify taken children actually fit within the budget.
        // sliceByHeight's first-row guarantee may produce oversized results
        // when a single child row exceeds the available space — in that case,
        // move the entire parent row to the next page instead of overflowing.
        const takenChildrenH = measureVisibleHeight(
          childResult.taken, expanded, rowKey, offsets, rowHFn
        );
        if (takenChildrenH > remainingBudget && i > 0) {
          return {
            taken: rows.slice(0, i),
            remaining: rows.slice(i),
            remainingOffsets: { [parentKey]: origIdx }
          };
        }

        const takenRow = { ...row, children: childResult.taken };
        const taken = [...rows.slice(0, i), takenRow];

        if (childResult.remaining.length > 0) {
          const remainingRow = { ...row, children: childResult.remaining };
          return {
            taken,
            remaining: [remainingRow, ...rows.slice(i + 1)],
            remainingOffsets: {
              [parentKey]: origIdx,
              ...childResult.remainingOffsets
            }
          };
        }

        return {
          taken,
          remaining: rows.slice(i + 1),
          remainingOffsets: { [parentKey]: origIdx + 1 }
        };
      }
    }

    // No room for parent + any children
    if (i > 0) {
      return {
        taken: rows.slice(0, i),
        remaining: rows.slice(i),
        remainingOffsets: { [parentKey]: origIdx }
      };
    }

    // First row doesn't fit — take it anyway to avoid infinite loop
    used += totalCost;
  }

  return {
    taken: rows,
    remaining: [],
    remainingOffsets: {}
  };
}

// ─── Card height estimation ─────────────────────────────────────────────────

/** PreviewCardWrapper adds a 1px border (top + bottom = 2px) per card. */
const CARD_BORDER_PX = 2;

/**
 * Estimate a non-table card's rendered height.
 * Uses the explicit `heightPx` when set, otherwise falls back to
 * conservative type-based defaults that are much closer to reality
 * than a flat 300px.
 */
function estimateCardHeight(card: CanvasCard): number {
  if (card.heightPx != null) return card.heightPx;
  switch (card.config.type) {
    case "kpi":
      return 90;
    case "summary":
      return 110;
    case "text":
      return 120;
    case "chart":
    case "exposure":
      return 240;
    case "cashflow":
    case "performance":
    case "unfunded":
    case "macroBrief":
      return 200;
    case "benchmark":
    case "dataFreshness":
      return 160;
    case "ai_card":
      return 150;
    default:
      return 200;
  }
}

/**
 * Group a flat list of cards into visual rows using the same width-accumulation
 * logic that PageView and PreviewMode use for rendering.
 */
function groupIntoVisualRows(cards: CanvasCard[]): CanvasCard[][] {
  const rows: CanvasCard[][] = [];
  let current: CanvasCard[] = [];
  let w = 0;
  for (const card of cards) {
    if (w > 0 && w + card.widthPercent > 100.01) {
      rows.push(current);
      current = [];
      w = 0;
    }
    current.push(card);
    w += card.widthPercent;
  }
  if (current.length > 0) rows.push(current);
  return rows;
}

// ─── Public API ──────────────────────────────────────────────────────────────

export function isTableCardWithData(card: CanvasCard): card is CanvasCard & { data: DataTableCardData } {
  if (card.config.type !== "table" || !card.data) return false;
  const d = card.data as DataTableCardData;
  return Array.isArray(d.data) && Array.isArray(d.columns);
}

export interface SplitResult {
  pages: CanvasCard[][];
  pageMeta: SplitPageMeta[];
  /** Per-card index offsets for DataTableCard key generation. */
  cardOffsets: Record<string, Record<string, number>>;
  /** Maps continuation card IDs back to their original card ID. */
  originalCardIds: Record<string, string>;
}

export function splitPagesForOverflow(
  groupedPages: CanvasCard[][],
  pages: ReportPage[],
  pageHeight: number,
  pageWidth: number,
  expandedRows?: Record<string, Set<string>>
): SplitResult {
  const resolvedPages: CanvasCard[][] = [];
  const resolvedMeta: SplitPageMeta[] = [];
  const cardOffsets: Record<string, Record<string, number>> = {};
  const originalCardIds: Record<string, string> = {};

  for (let pageIdx = 0; pageIdx < groupedPages.length; pageIdx++) {
    const pageCards = groupedPages[pageIdx];
    const page = pages[pageIdx];
    const layout = page.layout;

    if (layout === "title") {
      resolvedPages.push(pageCards);
      resolvedMeta.push({ ...page, isContinuation: false, originalPageIndex: pageIdx });
      continue;
    }

    const headerH = layout === "header-content" ? 180 : layout === "section" ? 150 : 0;
    const footerH = 52;
    const padVertical = 80; // 32px top + 48px bottom content padding
    const safetyBuffer = 20; // accounts for estimation errors, borders, etc.
    const rowGap = 20;
    const contentOnlyAvailH = pageHeight - footerH - padVertical - safetyBuffer;

    let currentCards: CanvasCard[] = [];
    let usedH = 0;
    let currentAvailH = pageHeight - headerH - footerH - padVertical - safetyBuffer;
    let currentMeta: SplitPageMeta = { ...page, isContinuation: false, originalPageIndex: pageIdx };

    const flushPage = () => {
      if (currentCards.length > 0) {
        resolvedPages.push(currentCards);
        resolvedMeta.push(currentMeta);
      }
      currentCards = [];
      usedH = 0;
      currentAvailH = contentOnlyAvailH;
      currentMeta = { layout: "content" as PageLayout, isContinuation: true, originalPageIndex: pageIdx };
    };

    // Group cards into visual rows so side-by-side cards share one row height
    const visualRows = groupIntoVisualRows(pageCards);

    for (const visualRow of visualRows) {
      // Check if this row contains a splittable table
      const tableCardIdx = visualRow.findIndex((c) => isTableCardWithData(c));

      if (tableCardIdx === -1) {
        // ── Non-table row: height = tallest card in the row + border ──
        const rowH = Math.max(...visualRow.map((c) => estimateCardHeight(c))) + CARD_BORDER_PX;
        const gap = currentCards.length > 0 ? rowGap : 0;

        if (currentCards.length > 0 && usedH + gap + rowH > currentAvailH) {
          flushPage();
        }
        if (currentCards.length > 0) usedH += rowGap;
        currentCards.push(...visualRow);
        usedH += rowH;
        continue;
      }

      // ── Row contains a table card ──
      const card = visualRow[tableCardIdx];
      const companionCards = visualRow.filter((_, i) => i !== tableCardIdx);
      const companionH =
        companionCards.length > 0 ? Math.max(...companionCards.map((c) => estimateCardHeight(c))) + CARD_BORDER_PX : 0;

      const tableData = (card as CanvasCard & { data: DataTableCardData }).data;
      const tableRows = tableData.data;

      if (tableRows.length === 0) {
        const rowH = Math.max(60, companionH) + CARD_BORDER_PX;
        const gap = currentCards.length > 0 ? rowGap : 0;
        if (currentCards.length > 0 && usedH + gap + rowH > currentAvailH) {
          flushPage();
        }
        if (currentCards.length > 0) usedH += rowGap;
        currentCards.push(...visualRow);
        usedH += rowH;
        continue;
      }

      // Compute per-row height metrics for this table card
      const tier = getTableTier(card.widthPercent);
      const { bodyFont, headerFont, padV, padH } = TABLE_TIER_METRICS[tier];
      const contentPadH = 96; // 48px left + 48px right (preview mode)
      const cardWidthPx = (pageWidth - contentPadH) * (card.widthPercent / 100);
      const colKeys = tableData.columns.map((col: string | { key: string }) =>
        typeof col === "string" ? col : col.key
      );
      const colCount = colKeys.length;
      const colWidthPx = cardWidthPx / Math.max(colCount, 1);
      const textWidthPx = Math.max(colWidthPx - 2 * padH, 10);
      const charWidth = bodyFont * 0.6;
      const singleLineH = Math.ceil(bodyFont * 1.5);

      const rowHFn = (r: TableRow) => estimateRowH(r, colKeys, textWidthPx, charWidth, singleLineH, padV);

      const headerRowH = getHeaderRowH(headerFont, padV);
      const tablePad = 8;

      const cardExpanded = expandedRows?.[card.id] ?? EMPTY_SET;
      const totalContentH = measureVisibleHeight(tableRows, cardExpanded, "root", EMPTY_OFFSETS, rowHFn);
      const totalTableH = tablePad + headerRowH + totalContentH + CARD_BORDER_PX;
      const gap = currentCards.length > 0 ? rowGap : 0;
      const spaceForRow = currentAvailH - usedH - gap;

      // If the whole table (and companion cards) fit, add the row
      if (Math.max(totalTableH, companionH) <= spaceForRow) {
        usedH += gap + Math.max(totalTableH, companionH);
        currentCards.push(...visualRow);
        continue;
      }

      // ── Split required ──
      const spaceForTableRows = spaceForRow - tablePad - headerRowH - CARD_BORDER_PX;

      let remaining: TableRow[];
      let currentOffsets: Record<string, number> = {};

      if (spaceForTableRows > 0) {
        const sliceResult = sliceByHeight(
          tableRows,
          spaceForTableRows,
          cardExpanded,
          "root",
          EMPTY_OFFSETS,
          rowHFn
        );
        if (sliceResult.taken.length > 0) {
          // First chunk + companion cards stay on the current page
          currentCards.push(...companionCards);
          currentCards.push({
            ...card,
            data: { columns: tableData.columns, data: sliceResult.taken }
          });
          remaining = sliceResult.remaining;
          currentOffsets = sliceResult.remainingOffsets;
        } else {
          remaining = tableRows;
        }
      } else {
        remaining = tableRows;
      }

      // If nothing fit on this page, companions go with the first chunk on next page
      if (remaining === tableRows) {
        flushPage();
        // Try fitting on the fresh page
        const freshSpace = currentAvailH - tablePad - headerRowH - CARD_BORDER_PX;
        const freshSlice = sliceByHeight(tableRows, freshSpace, cardExpanded, "root", EMPTY_OFFSETS, rowHFn);
        currentCards.push(...companionCards);
        if (freshSlice.taken.length > 0) {
          currentCards.push({
            ...card,
            data: { columns: tableData.columns, data: freshSlice.taken }
          });
          remaining = freshSlice.remaining;
          currentOffsets = freshSlice.remainingOffsets;
        } else {
          // Table doesn't even fit a fresh page — push it all to avoid infinite loop
          currentCards.push(card);
          remaining = [];
        }
      } else {
        flushPage();
      }

      let chunkIdx = 1;

      while (remaining.length > 0) {
        const contSpaceForRows = currentAvailH - tablePad - headerRowH - CARD_BORDER_PX;

        const contResult = sliceByHeight(remaining, contSpaceForRows, cardExpanded, "root", currentOffsets, rowHFn);

        const contCardId = `${card.id}-cont-${chunkIdx}`;
        const contCard: CanvasCard = {
          ...card,
          id: contCardId,
          data: { columns: tableData.columns, data: contResult.taken }
        };

        if (Object.keys(currentOffsets).length > 0) {
          cardOffsets[contCardId] = currentOffsets;
        }
        originalCardIds[contCardId] = card.id;

        if (contResult.remaining.length > 0) {
          resolvedPages.push([contCard]);
          resolvedMeta.push({ layout: "content" as PageLayout, isContinuation: true, originalPageIndex: pageIdx });
        } else {
          currentCards.push(contCard);
          const chunkContentH = measureVisibleHeight(
            contResult.taken,
            cardExpanded,
            "root",
            currentOffsets,
            rowHFn
          );
          usedH = tablePad + headerRowH + chunkContentH + CARD_BORDER_PX;
        }

        remaining = contResult.remaining;
        currentOffsets = contResult.remainingOffsets;
        chunkIdx++;
      }
    }

    if (currentCards.length > 0 || !currentMeta.isContinuation) {
      resolvedPages.push(currentCards);
      resolvedMeta.push(currentMeta);
    }
  }

  return { pages: resolvedPages, pageMeta: resolvedMeta, cardOffsets, originalCardIds };
}
